import type { TranslationFillResult } from '@testflow/shared';
import { request } from './api.js';

export interface CatalogProcess {
  process_id: string;
  module_id: string | null;
  name_de: string;
  name_en: string;
  // #63/#64: DE per KI erzeugt (EN-Quell-Import) — Gegenstueck zu name_en_ai (DE-Quell-Import).
  name_de_ai: boolean;
  name_en_ai: boolean;
  // #49 (LNG-4): Prüfmarkierung aus dem Import — der Server liefert sie längst (SELECT *), nur der
  // Typ deklarierte sie bisher nicht, weshalb sie im UI nicht angezeigt werden konnte.
  translation_review: boolean;
  sap_scope_item_id: string | null;
  // #112 (A6): fachliche ID aus der Quelldatei ("WMI_09"). Nur lesbar — kein Bedienelement,
  // Korrekturweg ist der Neuimport. Bewusst getrennt von sap_scope_item_id (dort stehen
  // SAP-Scope-Items wie "BD9").
  source_ref: string | null;
  in_scope: boolean;
  notes: string | null;
  labels: string[];
}
export interface TreeProcess {
  process_id: string;
  name_de: string;
  name_en: string;
  // #112 (A6): wird neben der Prozess-Anzeige auch durchsucht — „WMI_" findet die ganze Gruppe.
  source_ref: string | null;
  in_scope: boolean;
  test_case_count: number;
}
export interface CatalogTree {
  nodes: { node_id: string; name: string; kind: string; processes: TreeProcess[] }[];
  unlabeled: TreeProcess[];
  /** Testfälle ohne Prozess — hängen an keinem Ast und brauchen deshalb eine eigene Gruppe. */
  without_process: number;
}
export interface Step {
  step_id: string;
  step_no: number;
  description_de: string;
  description_en: string;
  // #63/#64: DE per KI erzeugt (EN-Quell-Import).
  description_de_ai: boolean;
  description_en_ai: boolean;
  tcode: string | null;
  expected_output_de: string | null;
  expected_output_en: string | null;
  // #63/#64: DE per KI erzeugt (EN-Quell-Import).
  expected_output_de_ai: boolean;
  expected_output_en_ai: boolean;
  // #66: Schritt-Input ("Input Data") — symmetrisch zu expected_output_*, bidirektional.
  input_de: string | null;
  input_en: string | null;
  input_de_ai: boolean;
  input_en_ai: boolean;
  notes: string | null;
}
export interface TestCaseDetail {
  test_case_id: string;
  human_id: string;
  // #112 (A6): fachliche ID aus der Quelldatei — steht neben der TestFlow-ID (`ETO-00042 · WMI_09`)
  // und zaehlt in der Katalogsuche mit.
  source_ref: string | null;
  process_id: string | null;
  title_de: string;
  title_en: string;
  // #63/#64: DE per KI erzeugt (EN-Quell-Import) — Gegenstueck zu title_en_ai (DE-Quell-Import).
  title_de_ai: boolean;
  title_en_ai: boolean;
  translation_review: boolean;
  // #111: Übersetzungslücke (gleiche Regel wie der Katalogfilter) — steuert den Knopf
  // „Fehlende Übersetzungen ergänzen". Optional: ältere Server-Antworten im Cache kennen es nicht.
  translation_gap?: boolean;
  precondition_de: string | null;
  precondition_en: string | null;
  precondition_de_ai: boolean;
  precondition_en_ai: boolean;
  expected_de: string | null;
  expected_en: string | null;
  expected_de_ai: boolean;
  expected_en_ai: boolean;
  polarity: 'positiv' | 'negativ' | null;
  readiness: 'bereit' | 'in_arbeit' | 'nicht_im_scope';
  default_tester_id: string | null;
  default_tester_name: string | null;
  steps: Step[];
  e2e_memberships: { e2e_id: string; sequence: number }[];
  // #113 Task 11 (A7): die Herkunftszeile am Detail — „Teilprojekt · Modul · Prozess · E2E-Kette
  // (Schritt N von M)". Aufbereitet vom Server (services/testcase-origin.ts), NICHT aus
  // `e2e_memberships` ableitbar: dort stehen nur e2e_id und sequence, weder Kettenname noch
  // Kettenlaenge.
  //
  // Review-Befund Minor 6: optional, weil testcase-detail.tsx (OriginLine) bereits mit
  // `origin === undefined` rechnet — eine aeltere Server-Antwort kann waehrend eines Rollouts noch
  // ohne dieses Feld im react-query-Cache liegen. Als Pflichtfeld deklariert stand der Typ im
  // Widerspruch zur Laufzeit-Annahme, und der `if (!origin) return null` dort war toter Code, den
  // der Typprüfer nie erzwang. Jetzt erzwingt er ihn.
  origin?: TestCaseOrigin;
}
/** Ein Glied der Zeile: die Kette und die Stelle, an der dieser Testfall darin steht. */
export interface TestCaseOriginChain {
  e2e_id: string;
  // #144: ETO-ID der Kette — daraus wird die abgeleitete Glied-ID „DTP_2 · 3/10" gebildet.
  source_ref: string | null;
  human_id: string;
  name_de: string;
  name_en: string;
  /** 1-basierte Position in der SORTIERTEN Kette — nicht `sequence` (die kann Luecken haben). */
  step: number;
  total: number;
}
export interface TestCaseOrigin {
  // Review-Befund Minor 3 (Task 11): `node_id`/`process_id` werden von OriginLine heute nirgends
  // gelesen (Teilprojekt-Link braucht keinen Knoten, Prozess-Link baut auf `name` + `testCaseId`)
  // — bewusste Vorhaltung fuer die Folge-Deeplinks aus den „Offenen Zweifeln" des Task-11-Berichts
  // (`?process=<id>`, Knoten-Vorauswahl auf der Zuordnungsseite), keine vergessene Aufraeumung.
  teilprojekt: { node_id: string; name: string } | null;
  /** A7: „Modul nur, wenn gesetzt" — `processes.module_id` ist nullbar. Ohne ID: fuer `modules`
   *  gibt es im Frontend keine Ansicht und keinen absehbaren Deeplink (Minor 3). */
  modul: { name_de: string; name_en: string } | null;
  prozess: { process_id: string; name_de: string; name_en: string } | null;
  /** A6: ein Testfall kann in mehreren Ketten liegen — deshalb eine Liste. */
  chains: TestCaseOriginChain[];
}
/** #144: Kettenbezug eines Testfalls in der Liste — Kette + Position darin. */
export interface ChainRef {
  e2e_id: string;
  source_ref: string | null;
  human_id: string;
  step: number;
  total: number;
}
/** #143/#144: Zeile der Testfallliste (`GET .../catalog/testcases`) — Detail ohne Schritte,
 *  Mitgliedschaften und Herkunft, dafür mit den abgeleiteten Listenfeldern. */
export interface TestCaseListItem
  extends Omit<TestCaseDetail, 'steps' | 'e2e_memberships' | 'origin'> {
  source_import_job_id: string | null;
  source_filename: string | null;
  // #153: Herkunfts-Teilprojekt aus dem Import und Schrittzahl — für die Zuordnungsseite.
  source_teilprojekt: string | null;
  step_count: number;
  chain_refs: ChainRef[];
  translation_gap: boolean;
}
export interface E2eMemberView {
  test_case_id: string;
  sequence: number;
  human_id: string;
  // #112 (A6): die Testplan-Scope-Auswahl baut ihre Kettenzeilen aus dieser Sicht.
  source_ref: string | null;
  title_de: string;
  // #179: fuer die Anzeige in der Oberflaechensprache.
  title_en: string;
}
export interface E2eProcess {
  e2e_id: string;
  human_id: string;
  // #125 (Q3): Ketten-ID aus der Szenario-Datei (`P2P_3`), nur lesbar.
  source_ref: string | null;
  name_de: string;
  name_en: string;
  // #63/#64: DE per KI erzeugt (EN-Quell-Import).
  name_de_ai: boolean;
  name_en_ai: boolean;
  // #49 (LNG-4): siehe CatalogProcess.translation_review — vom Server geliefert, Typ ergänzt.
  translation_review: boolean;
  members: E2eMemberView[];
  // #112: der Stream, an dem die Kette haengt (zweite Sicht: Stream > E2E-Prozess). null = die
  // Kette haengt an keinem Stream und erscheint unter „Ohne Stream".
  node_id: string | null;
  node_name: string | null;
}
export interface GlossaryTerm {
  term_id: string;
  term_de: string;
  term_en: string;
  source: 'sap_standard' | 'kundenspezifisch';
  notes: string | null;
}
export interface TestDataRow {
  test_data_id: string;
  data_kind_id: string;
  value: string;
  description: string | null;
}
export interface ProjectRow {
  project_id: string;
  name: string;
  customer_id: string;
  customer_key: string;
  customer_name: string;
}

const base = (p: string) => `/v1/projects/${p}/catalog`;

export const catalogApi = {
  listProjects: () => request<ProjectRow[]>('/v1/projects'),
  tree: (p: string) => request<CatalogTree>(`${base(p)}/tree`),
  listProcesses: (p: string) => request<CatalogProcess[]>(`${base(p)}/processes`),
  createProcess: (
    p: string,
    body: Partial<CatalogProcess> & { name_de: string; node_id?: string | null },
  ) =>
    request<CatalogProcess>(`${base(p)}/processes`, { method: 'POST', body: JSON.stringify(body) }),
  updateProcess: (p: string, id: string, body: Record<string, unknown>) =>
    request<CatalogProcess>(`${base(p)}/processes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteProcess: (p: string, id: string) =>
    request<void>(`${base(p)}/processes/${id}`, { method: 'DELETE' }),
  listTestCases: (p: string, processId?: string) =>
    request<TestCaseListItem[]>(
      `${base(p)}/testcases${processId ? `?process_id=${processId}` : ''}`,
    ),
  getTestCase: (p: string, id: string) => request<TestCaseDetail>(`${base(p)}/testcases/${id}`),
  createTestCase: (
    p: string,
    body: { process_id: string | null } & Partial<{
      title_de: string;
      title_en: string;
      precondition_de: string | null;
      precondition_en: string | null;
      expected_de: string | null;
      expected_en: string | null;
      polarity: 'positiv' | 'negativ' | null;
      readiness: 'bereit' | 'in_arbeit' | 'nicht_im_scope';
      default_tester_id: string | null;
    }> & { title_de: string },
  ) =>
    request<TestCaseDetail>(`${base(p)}/testcases`, { method: 'POST', body: JSON.stringify(body) }),
  updateTestCase: (p: string, id: string, body: Record<string, unknown>) =>
    request<TestCaseDetail>(`${base(p)}/testcases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteTestCase: (p: string, id: string) =>
    request<void>(`${base(p)}/testcases/${id}`, { method: 'DELETE' }),
  // #111: ergänzt fehlende Übersetzungen per KI. 200 auch bei Teilerfolg (failure gesetzt);
  // 429/502/503 (nichts geschrieben) kommen als ApiError an.
  fillTranslations: (p: string, id: string, mode: 'single' | 'bulk') =>
    request<TranslationFillResult>(`${base(p)}/testcases/${id}/translation-fill`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),
  addStep: (
    p: string,
    tcId: string,
    body: {
      description_de: string;
      description_en?: string;
      tcode?: string | null;
      expected_output_de?: string | null;
      expected_output_en?: string | null;
      input_de?: string | null;
      input_en?: string | null;
      notes?: string | null;
    },
  ) =>
    request<Step>(`${base(p)}/testcases/${tcId}/steps`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateStep: (p: string, stepId: string, body: Record<string, unknown>) =>
    request<Step>(`${base(p)}/steps/${stepId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  reorderSteps: (p: string, tcId: string, stepIds: string[]) =>
    request<{ ok: true }>(`${base(p)}/testcases/${tcId}/steps/reorder`, {
      method: 'POST',
      body: JSON.stringify({ step_ids: stepIds }),
    }),
  deleteStep: (p: string, stepId: string) =>
    request<void>(`${base(p)}/steps/${stepId}`, { method: 'DELETE' }),
  listE2e: (p: string) => request<E2eProcess[]>(`${base(p)}/e2e`),
  // TF-006 Kante C hat hier bewusst KEINEN Client: Das Umhaengen einer Kette laeuft
  // ausschliesslich ueber die Zuordnungsseite (POST /assignment/apply). Zwei Wege fuer dieselbe
  // Operation hatten unterschiedlich gewirkt — der eine widerrief Bestaetigungen (A8), der andere
  // nicht. Die frueher hier stehende `setE2eStream`-Methode und ihre Serverroute sind entfallen.
  createE2e: (p: string, body: { name_de: string; name_en?: string }) =>
    request<E2eProcess>(`${base(p)}/e2e`, { method: 'POST', body: JSON.stringify(body) }),
  updateE2e: (p: string, id: string, body: Record<string, unknown>) =>
    request<E2eProcess>(`${base(p)}/e2e/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  setE2eMembers: (p: string, id: string, members: { test_case_id: string; sequence: number }[]) =>
    request<{ ok: true }>(`${base(p)}/e2e/${id}/members`, {
      method: 'PUT',
      body: JSON.stringify({ members }),
    }),
  deleteE2e: (p: string, id: string) => request<void>(`${base(p)}/e2e/${id}`, { method: 'DELETE' }),
  listGlossary: (p: string) => request<GlossaryTerm[]>(`${base(p)}/glossary`),
  createGlossary: (
    p: string,
    body: { term_de: string; term_en: string; source?: string; notes?: string | null },
  ) => request<GlossaryTerm>(`${base(p)}/glossary`, { method: 'POST', body: JSON.stringify(body) }),
  updateGlossary: (p: string, id: string, body: Record<string, unknown>) =>
    request<GlossaryTerm>(`${base(p)}/glossary/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteGlossary: (p: string, id: string) =>
    request<void>(`${base(p)}/glossary/${id}`, { method: 'DELETE' }),
  listTestData: (p: string, kindId?: string) =>
    request<TestDataRow[]>(
      `${base(p)}/testdata${kindId ? `?data_kind_id=${encodeURIComponent(kindId)}` : ''}`,
    ),
  createTestData: (
    p: string,
    body: { data_kind_id: string; value: string; description?: string | null },
  ) => request<TestDataRow>(`${base(p)}/testdata`, { method: 'POST', body: JSON.stringify(body) }),
  updateTestData: (
    p: string,
    id: string,
    body: Partial<{ data_kind_id: string; value: string; description: string | null }>,
  ) =>
    request<TestDataRow>(`${base(p)}/testdata/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteTestData: (p: string, id: string) =>
    request<void>(`${base(p)}/testdata/${id}`, { method: 'DELETE' }),
};
